import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Package, Plus, Wrench, User, Calendar, DollarSign, AlertCircle } from 'lucide-react';

import API_URL from '../utils/api';

const Assets = () => {
    const [assets, setAssets] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [formData, setFormData] = useState({
        name: '', code: '', asset_type: 'fixed', category: '', description: '', purchase_date: '', purchase_price: '', serial_number: '', vendor: '', warranty_expiry: '', location: ''
    });
    const [assignData, setAssignData] = useState({ employee_id: '', assigned_date: '', notes: '' });

    useEffect(() => { fetchData(); }, [filterType, filterStatus]);

    const fetchData = async () => {
        try {
            let url = `${API_URL}/api/assets`;
            const params = [];
            if (filterType) params.push(`asset_type=${filterType}`);
            if (filterStatus) params.push(`status=${filterStatus}`);
            if (params.length) url += `?${params.join('&')}`;

            const [assetsRes, empRes, summaryRes] = await Promise.all([
                axios.get(url, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
                axios.get(`${API_URL}/api/assets/dashboard/summary`, { withCredentials: true })
            ]);
            setAssets(assetsRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
            setSummary(summaryRes.data);
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                purchase_price: parseFloat(formData.purchase_price) || 0,
                current_value: parseFloat(formData.purchase_price) || 0,
                purchase_date: formData.purchase_date ? new Date(formData.purchase_date).toISOString() : null,
                warranty_expiry: formData.warranty_expiry ? new Date(formData.warranty_expiry).toISOString() : null
            };
            await axios.post(`${API_URL}/api/assets`, payload, { withCredentials: true });
            setIsDialogOpen(false);
            setFormData({ name: '', code: '', asset_type: 'fixed', category: '', description: '', purchase_date: '', purchase_price: '', serial_number: '', vendor: '', warranty_expiry: '', location: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating asset');
        }
    };

    const handleAssign = async (e) => {
        e.preventDefault();
        if (!selectedAsset) return;
        try {
            await axios.post(`${API_URL}/api/assets/${selectedAsset._id}/assign`, {
                ...assignData,
                asset_id: selectedAsset._id,
                assigned_date: new Date(assignData.assigned_date).toISOString()
            }, { withCredentials: true });
            setAssignDialogOpen(false);
            setSelectedAsset(null);
            setAssignData({ employee_id: '', assigned_date: '', notes: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error assigning asset');
        }
    };

    const handleReturn = async (assetId) => {
        if (!window.confirm('Return this asset?')) return;
        try {
            await axios.post(`${API_URL}/api/assets/${assetId}/return`, {}, { withCredentials: true });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error returning asset');
        }
    };

    const getStatusColor = (status) => {
        const colors = { available: 'bg-green-100 text-green-800 border-green-200', assigned: 'bg-blue-100 text-blue-800 border-blue-200', maintenance: 'bg-amber-100 text-amber-800 border-amber-200', retired: 'bg-slate-100 text-slate-800 border-slate-200' };
        return colors[status] || colors.available;
    };

    const getTypeColor = (type) => {
        const colors = { fixed: 'bg-purple-100 text-purple-800', movable: 'bg-blue-100 text-blue-800', consumable: 'bg-green-100 text-green-800' };
        return colors[type] || colors.fixed;
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading assets...</div></div>;

    return (
        <div className="space-y-6" data-testid="assets-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Asset Management</h1>
                    <p className="text-slate-500 mt-1">Track and manage company assets</p>
                </div>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-asset-btn"><Plus className="h-4 w-4 mr-2" />Add Asset</Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Add New Asset</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Track an asset — type, serial, assignment and value.</DialogDescription></DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Asset Name</Label>
                                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="rounded-none mt-1" required />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code</Label>
                                    <Input value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} className="rounded-none mt-1" placeholder="AST-001" required />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
                                    <Select value={formData.asset_type} onValueChange={(v) => setFormData({...formData, asset_type: v})}>
                                        <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent className="rounded-none">
                                            <SelectItem value="fixed">Fixed</SelectItem>
                                            <SelectItem value="movable">Movable</SelectItem>
                                            <SelectItem value="consumable">Consumable</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</Label>
                                    <Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="rounded-none mt-1" placeholder="Laptop, Furniture, etc." required />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Serial Number</Label>
                                    <Input value={formData.serial_number} onChange={(e) => setFormData({...formData, serial_number: e.target.value})} className="rounded-none mt-1" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Vendor</Label>
                                    <Input value={formData.vendor} onChange={(e) => setFormData({...formData, vendor: e.target.value})} className="rounded-none mt-1" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Purchase Date</Label>
                                    <Input type="date" value={formData.purchase_date} onChange={(e) => setFormData({...formData, purchase_date: e.target.value})} className="rounded-none mt-1" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Purchase Price</Label>
                                    <Input type="number" value={formData.purchase_price} onChange={(e) => setFormData({...formData, purchase_price: e.target.value})} className="rounded-none mt-1" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Warranty Expiry</Label>
                                    <Input type="date" value={formData.warranty_expiry} onChange={(e) => setFormData({...formData, warranty_expiry: e.target.value})} className="rounded-none mt-1" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Location</Label>
                                    <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="rounded-none mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
                                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="rounded-none mt-1" rows={2} />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-none" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" className="bg-brand hover:bg-brand rounded-none">Add Asset</Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                    {[
                        { label: 'Total', value: summary.total, color: 'text-slate-900' },
                        { label: 'Available', value: summary.available, color: 'text-green-600' },
                        { label: 'Assigned', value: summary.assigned, color: 'text-brand' },
                        { label: 'Maintenance', value: summary.maintenance, color: 'text-amber-600' },
                        { label: 'Fixed', value: summary.fixed, color: 'text-purple-600' },
                        { label: 'Total Value', value: `$${(summary.total_value || 0).toLocaleString()}`, color: 'text-green-600' }
                    ].map((stat, i) => (
                        <Card key={i} className="rounded-none border-slate-200">
                            <CardContent className="p-4 text-center">
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{stat.label}</p>
                                <p className={`text-2xl font-black mt-1 ${stat.color}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{stat.value}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Card className="rounded-none border-slate-200">
                <CardHeader className="border-b border-slate-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Package className="h-5 w-5 text-brand" />All Assets
                        </CardTitle>
                        <div className="flex gap-2">
                            <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                                <SelectTrigger className="w-[130px] rounded-none"><SelectValue placeholder="All Types" /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                    <SelectItem value="all">All Types</SelectItem>
                                    <SelectItem value="fixed">Fixed</SelectItem>
                                    <SelectItem value="movable">Movable</SelectItem>
                                    <SelectItem value="consumable">Consumable</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                                <SelectTrigger className="w-[130px] rounded-none"><SelectValue placeholder="All Status" /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="available">Available</SelectItem>
                                    <SelectItem value="assigned">Assigned</SelectItem>
                                    <SelectItem value="maintenance">Maintenance</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50">
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Asset</th>
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Category</th>
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Assigned To</th>
                                <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Value</th>
                                <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                                <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assets.map((asset) => (
                                <tr key={asset._id} className="border-b border-slate-200 hover:bg-slate-50">
                                    <td className="p-4">
                                        <p className="font-medium text-slate-900">{asset.name}</p>
                                        <p className="text-sm text-slate-500">{asset.code}</p>
                                    </td>
                                    <td className="p-4"><Badge className={`rounded-none ${getTypeColor(asset.asset_type)}`}>{asset.asset_type}</Badge></td>
                                    <td className="p-4 text-slate-700">{asset.category}</td>
                                    <td className="p-4 text-slate-700">{asset.assigned_to_name || '-'}</td>
                                    <td className="p-4 text-right font-medium">${(asset.current_value || 0).toLocaleString()}</td>
                                    <td className="p-4 text-center"><Badge className={`rounded-none ${getStatusColor(asset.status)}`}>{asset.status}</Badge></td>
                                    <td className="p-4 text-right">
                                        {asset.status === 'available' && (
                                            <Button size="sm" className="rounded-none bg-brand hover:bg-brand" onClick={() => { setSelectedAsset(asset); setAssignDialogOpen(true); }}>Assign</Button>
                                        )}
                                        {asset.status === 'assigned' && (
                                            <Button size="sm" variant="outline" className="rounded-none" onClick={() => handleReturn(asset._id)}>Return</Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {assets.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-slate-500">No assets found</td></tr>}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                <DialogContent className="rounded-none">
                    <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Assign Asset</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Track an asset — type, serial, assignment and value.</DialogDescription></DialogHeader>
                    <form onSubmit={handleAssign} className="space-y-4 mt-4">
                        <p className="text-slate-600">Assigning: <strong>{selectedAsset?.name}</strong> ({selectedAsset?.code})</p>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Employee</Label>
                            <Select value={assignData.employee_id} onValueChange={(v) => setAssignData({...assignData, employee_id: v})}>
                                <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                                <SelectContent className="rounded-none">{employees.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Assigned Date</Label>
                            <Input type="date" value={assignData.assigned_date} onChange={(e) => setAssignData({...assignData, assigned_date: e.target.value})} className="rounded-none mt-1" required />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
                            <Textarea value={assignData.notes} onChange={(e) => setAssignData({...assignData, notes: e.target.value})} className="rounded-none mt-1" rows={2} />
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-brand hover:bg-brand rounded-none">Assign Asset</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Assets;
